// Command registry - imports and merges all command modules

import type { CommandResult, CommandContext, CommandHandler } from './types';
import { THEMES, type ThemeName } from '../themes';
import { generalCommands } from './general';
import { systemCommands } from './system';
import { infoCommands } from './info';
import { funCommands } from './fun';
import { languageCommands } from './language';
import { aiCommands } from './ai';
import { captureEvent } from '$lib/analytics/posthog';

// Command categories for analytics
const commandCategories: Record<string, string> = {
	// General
	help: 'help',
	commands: 'help',
	games: 'help',
	animations: 'help',
	tools: 'help',
	// System
	clear: 'system',
	exit: 'system',
	version: 'system',
	// Info
	stack: 'info',
	integrations: 'info',
	culture: 'info',
	how: 'info',
	blog: 'info',
	jobs: 'info',
	careers: 'info',
	challenge: 'info',
	privacy: 'info',
	// Games
	snake: 'game',
	tetris: 'game',
	pong: 'game',
	guess: 'game',
	breakout: 'game',
	tictactoe: 'game',
	typing: 'game',
	// Animations
	matrix: 'animation',
	fireworks: 'animation',
	hack: 'animation',
	glitch: 'animation',
	rain: 'animation',
	particles: 'animation',
	typewriter: 'animation',
	nyancat: 'animation',
	coffee: 'animation',
	// Tools
	pomodoro: 'tool',
	timer: 'tool',
	json: 'tool',
	// Effects
	spin: 'effect',
	flip: 'effect',
	barrel: 'effect',
	crt: 'effect',
	// Fun
	joke: 'fun',
	fortune: 'fun',
	cowsay: 'fun',
	ascii: 'fun',
	rickroll: 'fun',
	weather: 'fun',
	// Theme
	theme: 'theme',
	themes: 'theme',
	// Language
	lang: 'language',
	english: 'language',
	norwegian: 'language',
	// AI
	chat: 'ai',
	review: 'ai'
};

// Alias map: alias -> canonical command name
const aliases: Record<string, string> = {
	// Norwegian translations
	vær: 'weather',
	regn: 'rain',
	partikler: 'particles',
	skrivemaskin: 'typewriter',
	kaffe: 'coffee',
	teknologi: 'stack',
	integrasjoner: 'integrations',
	kultur: 'culture',
	hvordan: 'how',
	artikler: 'blog',
	jobb: 'jobs',
	karriere: 'jobs',
	utfordring: 'challenge',
	personvern: 'privacy',
	norsk: 'norwegian',
	språk: 'lang',
	// English aliases
	tech: 'stack',
	method: 'how',
	rick: 'rickroll',
	arkanoid: 'breakout',
	ttt: 'tictactoe',
	speed: 'typing',
	type: 'typing',
	vhs: 'glitch',
	pomo: 'pomodoro',
	en: 'english',
	no: 'norwegian',
	language: 'lang'
};

/**
 * Formats theme list with current theme indicator.
 * Shared between theme and themes commands to avoid duplication.
 */
function formatThemeList(currentTheme: ThemeName): string {
	return Object.entries(THEMES)
		.map(([key, t]) => `  ${key.padEnd(12)} - ${t.name}${key === currentTheme ? ' (active)' : ''}`)
		.join('\n');
}

// Theme commands (need access to THEMES)
const themeCommands: Record<string, CommandHandler> = {
	theme: (args, context) => {
		const themeName = args[0]?.toLowerCase() as ThemeName;
		if (themeName && THEMES[themeName]) {
			context.setTheme(themeName);
			return {
				type: 'theme',
				themeName,
				lines: [`Theme switched to ${THEMES[themeName].name}`]
			};
		}
		return {
			type: 'text',
			lines: [
				'TERMINAL THEMES',
				'',
				'Available themes:',
				formatThemeList(context.currentTheme),
				'',
				`Current theme: ${THEMES[context.currentTheme].name}`,
				'',
				'Usage: theme <name>',
				'Example: theme synthwave'
			]
		};
	},

	themes: (_, context) => ({
		type: 'text',
		lines: [
			'AVAILABLE THEMES',
			'',
			formatThemeList(context.currentTheme),
			'',
			`Current: ${THEMES[context.currentTheme].name}`,
			'',
			'Usage: theme <name>'
		]
	})
};

const commands: Record<string, CommandHandler> = {
	...generalCommands,
	...systemCommands,
	...infoCommands,
	...funCommands,
	...languageCommands,
	...aiCommands,
	...themeCommands
};

// Phrase patterns checked before word-based lookup
const phrasePatterns: [RegExp, () => CommandResult][] = [
	[/^do a barrel\s*roll$/i, () => ({ type: 'effect', effect: 'barrel' })],
	[/^barrel\s*roll$/i, () => ({ type: 'effect', effect: 'barrel' })],
	[/^do a spin$/i, () => ({ type: 'effect', effect: 'spin' })],
	[/^do a flip$/i, () => ({ type: 'effect', effect: 'flip' })]
];

/** Parse and execute a terminal command, returning the result or falling through to AI chat. */
export function executeCommand(input: string, context: CommandContext): CommandResult | null {
	const trimmed = input.trim();
	const lower = trimmed.toLowerCase();

	if (!lower) return null;

	// Check phrase patterns first
	for (const [pattern, handler] of phrasePatterns) {
		if (pattern.test(lower)) {
			return handler();
		}
	}

	const parts = lower.split(/\s+/);
	const commandName = aliases[parts[0]] || parts[0]; // Resolve alias
	const args = parts.slice(1);

	const handler = commands[commandName];
	if (handler) {
		const category = commandCategories[commandName] || 'unknown';
		captureEvent('terminal_command_executed', { command: commandName, category });
		return handler(args, context);
	}

	// Unrecognized commands fall through to AI chat
	return {
		type: 'chat',
		lines: [trimmed]
	};
}

export { commands };
